import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid } from '@/components/KstDataGrid';

import type {
  KstDataGridColumn,
  KstDataGridPager,
  KstDataGridRows,
} from '@/components/KstDataGrid';

import { api } from '@/services/api-class';
import { hasStringValue } from '@/utils/legacy-utils';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const displayNocValuesForGridTemplate = _wg('displayNocValuesForGridTemplate');
const canCurrentUserAccessCaseReport = _wg('canCurrentUserAccessCaseReport');

interface CallCardHistoryGridProps {
  callLocFormattedAddress?: string;
  active?: boolean;
}

export function CallCardHistoryGrid({
  callLocFormattedAddress = '',
  active = false,
}: CallCardHistoryGridProps) {
  const { theme } = useTheme();

  // Call History
  const [callEntries, setCallEntries] = useState<any[] | null>(null);
  const [isLoadingCalls, setIsLoadingCalls] = useState(false);
  const [callCurrentPage, setCallCurrentPage] = useState(1);
  const [callTotalRecords, setCallTotalRecords] = useState(0);
  const [callTotalPages, setCallTotalPages] = useState(1);

  // Report History
  const [reportEntries, setReportEntries] = useState<any[] | null>(null);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [reportCurrentPage, setReportCurrentPage] = useState(1);
  const [reportTotalRecords, setReportTotalRecords] = useState(0);
  const [reportTotalPages, setReportTotalPages] = useState(1);

  const callPageSize = 20;
  const reportPageSize = 20;

  const callLocRef = useRef(callLocFormattedAddress);
  useEffect(() => { callLocRef.current = callLocFormattedAddress; }, [callLocFormattedAddress]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return (month < 10 ? '0' : '') + month + '/' +
           (day < 10 ? '0' : '') + day + '/' +
           year + ' ' +
           hours + ':' +
           (minutes < 10 ? '0' : '') + minutes + ' ' + ampm;
  }, []);

  const displayNocValues = useCallback((entry: any): string => {
    if (typeof (window as any).displayNocValuesForGridTemplate === 'function') {
      return displayNocValuesForGridTemplate(entry);
    }
    if (entry.natureOfCallValues && Array.isArray(entry.natureOfCallValues)) {
      return entry.natureOfCallValues.join(', ');
    }
    return '';
  }, []);

  const canAccessReport = useCallback((entry: any): boolean => {
    if (typeof (window as any).canCurrentUserAccessCaseReport === 'function') {
      return canCurrentUserAccessCaseReport(entry.author);
    }
    return true;
  }, []);

  // --- Call History Grid ---

  const callColumns = useMemo((): KstDataGridColumn[] => {
    return [
      {
        field: 'eventId', name: 'Event ID', size: '100px',
        template: (e: any) => `<a id="lnkEvent" title="${e.id}" href="#" class="link-primary">${e.eventId}</a>`,
      },
      {
        field: 'formattedDateTime', name: 'Date/Time', size: '160px',
        template: (e: any) => formatDateTime(e.callDateTime),
      },
      {
        field: 'natureOfCall', name: 'Nature of Call',
        template: (e: any) => displayNocValues(e),
      },
      {
        field: 'callDispositionValue', name: 'Call Disposition',
        template: (e: any) => e.callDispositionValue || 'Disposition not found.',
      },
    ];
  }, [formatDateTime, displayNocValues]);

  const callRows = useMemo((): KstDataGridRows => {
    return {
      entries: callEntries === null ? null : callEntries.map((e: any) => ({
        ...e,
        formattedDateTime: formatDateTime(e.callDateTime),
      })),
    };
  }, [callEntries, formatDateTime]);

  const callPager = useMemo((): KstDataGridPager => {
    return {
      curPage: callCurrentPage,
      numPages: callTotalPages,
      records: callTotalRecords,
      pageSize: callPageSize,
      isVisible: callTotalPages > 1,
    };
  }, [callCurrentPage, callTotalPages, callTotalRecords]);

  const loadCallHistory = useCallback((page: number = 1) => {
    if (!hasStringValue(callLocRef.current)) {
      setCallEntries([]);
      setIsLoadingCalls(false);
      return;
    }
    setIsLoadingCalls(true);
    const operation = {
      data: {
        page,
        take: callPageSize,
        skip: (page - 1) * callPageSize,
      },
    };
    api.callCardSearchByLocation(
      callLocRef.current,
      page,
      String(callPageSize),
      JSON.stringify(operation),
      (result: any) => {
        setCallEntries(result.entries || []);
        setCallTotalRecords(result.records || 0);
        setCallTotalPages(Math.ceil((result.records || 0) / callPageSize) || 1);
        setIsLoadingCalls(false);
      }
    );
  }, []);

  const goToCallPage = useCallback((page: number) => {
    if (page < 1 || page > callTotalPages) return;
    setCallCurrentPage(page);
    loadCallHistory(page);
  }, [callTotalPages, loadCallHistory]);

  // --- Report History Grid ---

  const reportColumns = useMemo((): KstDataGridColumn[] => {
    return [
      {
        field: 'caseId', name: 'Case ID', size: '100px',
        template: (e: any) => canAccessReport(e)
          ? `<a id="lnkCaseRptHist" href="#${e.id}" class="link-primary">${e.caseId}</a>`
          : `<span>${e.caseId}</span>`,
      },
      {
        field: 'formattedDateTime', name: 'Date/Time', size: '160px',
        template: (e: any) => formatDateTime(e.incidentOccurredFrom),
      },
      {
        field: 'incidentNature', name: 'Incident Nature',
        template: (e: any) => displayNocValues(e),
      },
      {
        field: 'incidentDescription', name: 'Incident Description',
        template: (e: any) => e.incidentDescription || '',
      },
    ];
  }, [formatDateTime, displayNocValues, canAccessReport]);

  const reportRows = useMemo((): KstDataGridRows => {
    return {
      entries: reportEntries === null ? null : reportEntries.map((e: any) => ({
        ...e,
        formattedDateTime: formatDateTime(e.incidentOccurredFrom),
      })),
    };
  }, [reportEntries, formatDateTime]);

  const reportPager = useMemo((): KstDataGridPager => {
    return {
      curPage: reportCurrentPage,
      numPages: reportTotalPages,
      records: reportTotalRecords,
      pageSize: reportPageSize,
      isVisible: reportTotalPages > 1,
    };
  }, [reportCurrentPage, reportTotalPages, reportTotalRecords]);

  const loadReportHistory = useCallback((page: number = 1) => {
    if (!hasStringValue(callLocRef.current)) {
      setReportEntries([]);
      setIsLoadingReports(false);
      return;
    }
    setIsLoadingReports(true);
    const operation = {
      data: {
        page,
        take: reportPageSize,
        skip: (page - 1) * reportPageSize,
      },
    };
    api.reportSearchByLocation(
      callLocRef.current,
      page,
      String(reportPageSize),
      JSON.stringify(operation),
      (result: any) => {
        setReportEntries(result.entries || []);
        setReportTotalRecords(result.records || 0);
        setReportTotalPages(Math.ceil((result.records || 0) / reportPageSize) || 1);
        setIsLoadingReports(false);
      }
    );
  }, []);

  const goToReportPage = useCallback((page: number) => {
    if (page < 1 || page > reportTotalPages) return;
    setReportCurrentPage(page);
    loadReportHistory(page);
  }, [reportTotalPages, loadReportHistory]);

  // Load history when tab becomes active
  useEffect(() => {
    if (active && hasStringValue(callLocFormattedAddress)) {
      setCallCurrentPage(1);
      setReportCurrentPage(1);
      loadCallHistory(1);
      loadReportHistory(1);
    }
  }, [active, callLocFormattedAddress, loadCallHistory, loadReportHistory]);

  // Expose refresh method
  useEffect(() => {
    (window as any).CallCardHistoryGrid_refresh = () => {
      setCallCurrentPage(1);
      setReportCurrentPage(1);
      loadCallHistory(1);
      loadReportHistory(1);
    };
    return () => { delete (window as any).CallCardHistoryGrid_refresh; };
  }, [loadCallHistory, loadReportHistory]);

  return (
    <div className={theme}>
      <KstDataGrid
        title="Call History"
        columns={callColumns}
        rows={callRows}
        pager={callPager}
        loading={isLoadingCalls}
        showcheckbox={false}
        mobiletitlefield="eventId"
        onGoToPage={goToCallPage}
      />
      <KstDataGrid
        title="Report History"
        columns={reportColumns}
        rows={reportRows}
        pager={reportPager}
        loading={isLoadingReports}
        showcheckbox={false}
        mobiletitlefield="caseId"
        onGoToPage={goToReportPage}
      />
    </div>
  );
}

(window as any).CallCardHistoryGridReact = CallCardHistoryGrid;
